import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Plus, Search, MoreVertical, Paperclip } from 'lucide-react';
import { ExpenseSummaryCards } from '@/components/fleet/ExpenseSummaryCards';
import { ExpenseFormSheet } from '@/components/fleet/ExpenseFormSheet';
import { ExpenseStatusBadge } from '@/components/fleet/ExpenseStatusBadge';
import { useExpenses, useDeleteExpense, useMarkAsPaid, Expense } from '@/hooks/use-expenses';
import { formatCurrency, formatDueDate } from '@/lib/expense-utils';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import { Skeleton } from '@/components/ui/skeleton';

export default function FleetExpenses() {
  const [statusFilter, setStatusFilter] = useState<'all' | 'pending' | 'overdue' | 'paid'>('all');
  const [search, setSearch] = useState('');
  const [sheetOpen, setSheetOpen] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState<Expense | undefined>();

  const { data: expenses, isLoading } = useExpenses({
    status: statusFilter,
    search,
  });

  const deleteExpense = useDeleteExpense();
  const markAsPaid = useMarkAsPaid();

  const handleEdit = (expense: Expense) => {
    setSelectedExpense(expense);
    setSheetOpen(true);
  };

  const handleDelete = async (id: string) => {
    if (confirm('Tem certeza que deseja excluir esta despesa?')) {
      await deleteExpense.mutateAsync(id);
    }
  };

  const handleMarkAsPaid = async (id: string) => {
    await markAsPaid.mutateAsync({
      id,
      paidAt: new Date().toISOString().split('T')[0],
    });
  };

  const handleNewExpense = () => {
    setSelectedExpense(undefined);
    setSheetOpen(true);
  };

  const filteredExpenses = expenses || [];
  const pendingCount = expenses?.filter((e) => e.status === 'pending' || e.status === 'overdue').length || 0;
  const paidCount = expenses?.filter((e) => e.status === 'paid').length || 0;
  const totalCount = expenses?.length || 0;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Despesas</h1>
          <p className="text-muted-foreground">Gerencie as despesas da sua frota</p>
        </div>
        <Button onClick={handleNewExpense}>
          <Plus className="h-4 w-4 mr-2" />
          Nova Despesa
        </Button>
      </div>

      <ExpenseSummaryCards />

      <div className="space-y-4">
        <Tabs value={statusFilter} onValueChange={(value: any) => setStatusFilter(value)}>
          <TabsList>
            <TabsTrigger value="all">
              Todos
              <Badge variant="secondary" className="ml-2">
                {totalCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="pending">
              Em aberto
              <Badge variant="secondary" className="ml-2">
                {pendingCount}
              </Badge>
            </TabsTrigger>
            <TabsTrigger value="paid">
              Pago
              <Badge variant="secondary" className="ml-2">
                {paidCount}
              </Badge>
            </TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por descrição, veículo ou motorista..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10"
          />
        </div>

        <div className="border rounded-lg">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-[40px]"></TableHead>
                <TableHead>Despesa</TableHead>
                <TableHead>Motorista</TableHead>
                <TableHead>Veículo</TableHead>
                <TableHead>Vencimento</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead className="w-[50px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={7}>
                      <Skeleton className="h-12 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              ) : filteredExpenses.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12 text-muted-foreground">
                    Nenhuma despesa encontrada
                  </TableCell>
                </TableRow>
              ) : (
                filteredExpenses.map((expense) => (
                  <TableRow key={expense.id} className="cursor-pointer hover:bg-muted/50">
                    <TableCell>
                      <ExpenseStatusBadge status={expense.status} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="font-medium">{expense.description || expense.fleet_expense_categories?.name}</p>
                          <p className="text-sm text-muted-foreground">
                            {expense.fleet_expense_categories?.name}
                          </p>
                        </div>
                        {expense.attachment_url && (
                          <Paperclip className="h-4 w-4 text-muted-foreground" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      {expense.fleet_drivers ? (
                        <div className="flex items-center gap-2">
                          <Avatar className="h-8 w-8">
                            <AvatarFallback className="text-xs">
                              {expense.fleet_drivers.name.substring(0, 2).toUpperCase()}
                            </AvatarFallback>
                          </Avatar>
                          <span className="text-sm">{expense.fleet_drivers.name}</span>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {expense.fleet_vehicles ? (
                        <div className="text-sm">
                          <p className="font-medium">{expense.fleet_vehicles.plate}</p>
                          <p className="text-muted-foreground">{expense.fleet_vehicles.model}</p>
                        </div>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell>
                      <span
                        className={
                          expense.status === 'overdue'
                            ? 'text-destructive font-medium'
                            : 'text-foreground'
                        }
                      >
                        {formatDueDate(expense.due_date, expense.status)}
                      </span>
                    </TableCell>
                    <TableCell className="text-right font-medium">
                      {formatCurrency(Number(expense.value))}
                    </TableCell>
                    <TableCell>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEdit(expense)}>
                            Editar
                          </DropdownMenuItem>
                          {expense.status !== 'paid' && (
                            <DropdownMenuItem onClick={() => handleMarkAsPaid(expense.id)}>
                              Marcar como paga
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => handleDelete(expense.id)}
                            className="text-destructive"
                          >
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <ExpenseFormSheet
        open={sheetOpen}
        onOpenChange={setSheetOpen}
        expense={selectedExpense}
      />
    </div>
  );
}
